import { action, makeObservable, computed } from 'mobx';
import { BaseStepViewStore } from 'core/stores';
import {
	notificationError,
	isSuperAdminOrganization,
	getDecimalPounds,
	getKilograms,
	getPounds,
	getOunces,
	getPoundsAndOuncesFromKilograms,
} from 'core/utils';
import { LookupStore } from 'modules/lookup/stores';
import { ChannelStore } from 'modules/channel/stores';
import { ApplicationStore } from 'platform/application/stores';
import { channelType, lookupRouteName, sourceOfChange, errorCode } from 'common/constants';
import { ConfirmModalParams } from 'core/models';
import { BaasicDropdownViewStore } from 'common/stores';
import ProductStore from './ProductStore';
import { CarrierStore } from 'modules/carrier/stores';
import _ from 'lodash';
import { CreateBox } from '../pages';
import { ProductEditForm } from 'modules/product/wizard/forms';
import { dimensionUnitConstants, weightUnitConstants } from 'common/constants/productConstants';
import {
	ProductEditGeneralStep,
	ProductEditDimensionsStep,
	ProductEditVirtualSKUStep,
} from 'modules/product/wizard/pages';

class ProductEditViewStore extends BaseStepViewStore {
	isActive = true;
	form = new ProductEditForm({
		onSuccess: async () => {
			this.onSubmit();
		},
		onError: () => {
			this.checkValidity();
			this.rootStore.notificationStore.error('PRODUCT.WIZARD.INVALIDFORM');
		},
	});
	newVirtualSkus = [];
	fetchedWarehouses = [];
	product = {};
	searchId = 0;
	displaySellerDropdown = false;

	applyToAllActiveOrdersConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			await this.createOrUpdateProduct(true);
		},
		onCancel: async () => {
			await this.createOrUpdateProduct(false);
		},
	});

	newProductEntryConfirm = new ConfirmModalParams({
		onCancel: async () => {
			this.rootStore.routerStore.goTo('master.app.main.product.list', {});
		},
	});

	conflictWarningModal = new ConfirmModalParams({
		onConfirm: async () => {
			await this.createOrUpdateProduct();
		},
	});

	constructor(rootStore) {
		super(rootStore, [
			{
				name: 'generalStep',
				title: 'PRODUCT.WIZARD.GENERALSTEP',
				component: ProductEditGeneralStep,
				isValid: true,
				order: 1,
			},
			{
				name: 'dimensionsStep',
				title: 'PRODUCT.WIZARD.DIMENSIONSSTEP',
				component: ProductEditDimensionsStep,
				isValid: true,
				order: 2,
			},
			{
				name: 'virtualSKUStep',
				title: 'PRODUCT.WIZARD.VIRTUALSKUSTEP',
				component: ProductEditVirtualSKUStep,
				isValid: true,
				order: 3,
			},
		]);
		makeObservable(this);
		const {
			orderDetailId,
			sku,
			channel,
			orderLineId, // ^^ not null when creating a new product from LinkOrderLine
			sellerId,
			productIdForPart, // ^^ not null when creating a new product from add product part modal
			id, // not null when edit
		} = rootStore.routerStore.routerState.params;
		Object.assign(this, {
			orderLineId,
			sku,
			channel,
			orderDetailId,
			productIdForPart,
		});
		this.productId = id;
		this.parentSellerId = sellerId;
		this.listStore = new ProductStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.applicationStore = new ApplicationStore(rootStore);
		this.initialize();
	}

	@action.bound
	async initialize() {
		this.suspend();
		if (isSuperAdminOrganization(this.rootStore)) this.displaySellerDropdown = true;
		if (this.productId) {
			this.product = await this.listStore.getProduct(this.productId, {
				embed: [
					'dimensionUnit',
					'weightUnit',
					'productStatus',
					'condition',
					'productCategory',
					'productVirtualSkus',
					'productVirtualSkus.channel',
					'productVirtualSkus.channel.channelType',
					'productVirtualSkus.productVirtualSkuCarriers',
					'productVirtualSkus.productVirtualSkuCarriers.carrier',
					'box',
					'seller',
					'productUpcs',
				],
			});
			this.populateForm();
		} else if (this.orderLineId && this.channel) {
			this.displaySellerDropdown = false;
			this.addVirtualSku(true);
		}
		await this.initializeDropdowns();

		if (this.productIdForPart) {
			this.form.$('generalStep.productPartQuantity').set('rules', 'required|integer|min:0|notZero');
		} else if (this.displaySellerDropdown && !this.productId) {
			this.form.$('generalStep.sellerId').set('rules', 'required');
		}

		this.setRules();
		this.resume();
	}

	@action.bound
	initializeDropdowns = async () => {
		if (!this.productId) {
			this.sellerDropdownStore = new BaasicDropdownViewStore({
				field: this.form.$('generalStep.sellerId'),
				defaultInitialValue: { path: 'value', value: this.parentSellerId },
				store: new ApplicationStore(this.rootStore),
				itemTemplate: {
					value: 'id',
					label: 'applicationTitle',
				},
				filterProperties: {
					sellerOnly: true,
				},
				localStorageHandlerKey: this.displaySellerDropdown ? 'selectedSeller' : null,
				initCallback: () => {
					if (this.sellerDropdownStore.value) {
						this.updateChannels(this.sellerDropdownStore.value);
					}
				},
				onChangeCallback: value => this.updateChannels(value),
			});
		}
		_.each(
			[
				'dimensionUnitDropdownStore',
				'weightUnitDropdownStore',
				'conditionDropdownStore',
				'productStatusDropdownStore',
				'productCategoryDropdownStore',
				'boxDropdownStore',
			],
			dropdownStore => {
				this[dropdownStore] = new BaasicDropdownViewStore();
			}
		);

		let tasks = [];
		tasks.push(
			this.dimensionUnitDropdownStore.initialize({
				field: this.form.$('dimensionsStep.dimensionUnitId'),
				value: _.get(this.product, 'dimensionUnit'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.dimensionUnit,
				defaultInitialValue: { path: 'extra.name', value: dimensionUnitConstants.inches },
			})
		);
		tasks.push(
			this.weightUnitDropdownStore.initialize({
				field: this.form.$('dimensionsStep.weightUnitId'),
				value: _.get(this.product, 'weightUnit'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.weightUnit,
				onChangeCallback: value => this.onWeightUnitChange(value),
				defaultInitialValue: { path: 'extra.name', value: weightUnitConstants.poundsAndOunces },
			})
		);
		tasks.push(
			this.conditionDropdownStore.initialize({
				field: this.form.$('generalStep.conditionId'),
				value: _.get(this.product, 'condition'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.condition,
				defaultInitialValue: { path: 'extra.name', value: 'New' },
			})
		);
		tasks.push(
			this.productStatusDropdownStore.initialize({
				field: this.form.$('generalStep.productStatusId'),
				value: _.get(this.product, 'productStatus'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.productStatus,
				defaultInitialValue: { path: 'extra.name', value: 'Available' },
			})
		);
		tasks.push(
			this.productCategoryDropdownStore.initialize({
				field: this.form.$('generalStep.productCategoryId'),
				value: _.get(this.product, 'productCategory'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.productCategory,
			})
		);
		tasks.push(
			this.boxDropdownStore.initialize({
				field: this.form.$('dimensionsStep.boxId'),
				value: _.get(this.product, 'box'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.box,
			})
		);
		await Promise.all(tasks);
	};

	@action.bound
	addUpcField = () => {
		this.form
			.$('generalStep.productUpcs')
			.add([{ id: '', upc: '', productId: this.productId ? this.productId : null }]);
	};

	@action.bound
	setRules = () => {
		this.form.$('dimensionsStep.automaticBox').set('disabled', this.form.$('generalStep.isShippable').value);
		if (this.form.$('generalStep.isShippable').value) {
			if (Object.keys(this.form.$('generalStep.productUpcs').get()).length === 0) {
				this.addUpcField();
			}
			this.form.setFieldRules(
				['dimensionsStep.length', 'dimensionsStep.width', 'dimensionsStep.height'],
				this.form.$('generalStep.isMultiProduct').value
					? 'numeric|notZero'
					: 'required|numeric|notZero|numericMinValue|decimalLength'
			);
			this.form.setFieldRules(
				['generalStep.conditionId', 'dimensionsStep.dimensionUnitId', 'dimensionsStep.weightUnitId'],
				'required'
			);

			// Weight Rules
			if (this.weightUnitDropdownStore.value.label == 'Kilograms') {
				this.form
					.$('dimensionsStep.weight')
					.set(
						'rules',
						this.form.$('generalStep.isMultiProduct').value
							? 'numeric|numericMinValue|decimalLength|notZero'
							: 'required|numeric|numericMinValue|decimalLength|notZero'
					);
				this.form.clearRules(['dimensionsStep.pounds', 'dimensionsStep.ounces']);
			} else {
				this.form.clearRules(['dimensionsStep.weight']);
				this.form.$('dimensionsStep.ounces').set('rules', 'numeric|integer');
				this.form
					.$('dimensionsStep.pounds')
					.set(
						'rules',
						this.form.$('dimensionsStep.ounces').value > 0
							? 'numeric|numericMinValue|decimalLength'
							: 'required|numeric|numericMinValue|decimalLength|notZero'
					);
			}
		} else {
			this.form.clearRules([
				'generalStep.conditionId',
				'dimensionsStep.length',
				'dimensionsStep.width',
				'dimensionsStep.height',
				'dimensionsStep.dimensionUnitId',
				'dimensionsStep.weightUnitId',
				'dimensionsStep.pounds',
				'dimensionsStep.ounces',
			]);

			this.form.$('generalStep.productUpcs').map(productUpc => {
				this.form.$('generalStep.productUpcs').del(productUpc.key);
			});
		}
	};

	@action.bound
	addVirtualSku = (populateChannel = false) => {
		this.searchId += 1;
		const field = this.form
			.$('virtualSKUStep')
			.$('productVirtualSkus')
			.add({
				fields: {
					sku: this.sku ? this.sku : '',
					channel:
						this.channel &&
						!this.form
							.$('virtualSKUStep.productVirtualSkus')
							.value.some(virtualSku => virtualSku.channel.value === this.channel.id)
							? { value: { value: this.channel.id, label: this.channel.name, extra: this.channel } }
							: null,
					description: '',
					id: '',
					searchId: this.searchId,
					postInventory: this.isActive,
					isActive: this.isActive,
					carriers: null,
					isNonPrime: false,
					useAmazonInventoryMerge: false,
				},
			});
		field.carrierDropdownStore = new BaasicDropdownViewStore({
			field: field.$('carrierIds'),
			isMulti: true,
			store: new CarrierStore(this.rootStore),
			filterProperties: {
				channelIds: field.$('channelId').value ? [field.$('channelId').value.value] : [],
			},
		});
		field.channelDropdownStore = new BaasicDropdownViewStore({
			field: field.$('channelId'),
			store: new ChannelStore(this.rootStore),
			fetchFunc: 'findChannel',
			filterProperties: { embed: ['channelType'], sellerIds: this.getSellerId ? [this.getSellerId] : undefined },
			onChangeCallback: () => this.onChannelChange(field),
			defaultInitialValue: populateChannel ? { path: 'value', value: this.channel.id } : null,
		});
		this.newVirtualSkus.push(field);
	};

	@action
	async onSubmit() {
		if (this.newVirtualSkus.length > 0) {
			this.applyToAllActiveOrdersConfirmModal.open({
				message: ['PRODUCTVIEW.AUTOMATICALLYLINKSKUS'],
			});
		} else {
			this.createOrUpdateProduct(false);
		}
	}

	@action.bound
	setVirtualSkuActivity = (pvsValue, postInventory) => {
		if (pvsValue != null && postInventory != null) {
			postInventory.value = pvsValue;
		}
	};

	@computed get getSellerId() {
		return _.get(this.product, 'sellerId')
			? this.product.sellerId
			: this.parentSellerId || _.get(this.sellerDropdownStore, 'value.value');
	}

	@action.bound
	populateForm = () => {
		this.form.updateForm(this.product, true);
		this.isActive = this.product.isActive;
		if (this.product.productVirtualSkus) {
			_.each(this.product.productVirtualSkus, (virtualSku, idx) => {
				const field = this.form.$(`virtualSKUStep.productVirtualSkus[${idx}]`);
				field.channelDropdownStore = new BaasicDropdownViewStore({
					field: field.$('channelId'),
					value: virtualSku.channel,
					store: new ChannelStore(this.rootStore),
					fetchFunc: 'findChannel',
					filterProperties: { embed: ['channelType'], sellerIds: this.getSellerId ? [this.getSellerId] : undefined },
					onChangeCallback: () => this.onChannelChange(field),
				});
				field.$('channelId').set('rules', 'required');
				field.carrierDropdownStore = new BaasicDropdownViewStore({
					field: field.$('carrierIds'),
					value: virtualSku.productVirtualSkuCarriers.map(vskuCarrier => vskuCarrier.carrier),
					isMulti: true,
					filterProperties: {
						channelIds: [field.$('channelId').value],
					},
					store: new CarrierStore(this.rootStore),
				});
			});
		}
		if (this.product.weightUnit && this.product.weightUnit.name == weightUnitConstants.poundsAndOunces) {
			this.form.$('dimensionsStep.pounds').value = getPounds(this.product.weight);
			this.form.$('dimensionsStep.ounces').value = getOunces(this.product.weight);
		}
		this.form.$('dimensionsStep.automaticBox').set(false);
	};

	@action
	createOrUpdateProduct = async applyToAllActiveOrders => {
		this.suspend();
		try {
			this.product = this.form.getValuesFromSteps();
			let virtualSkus = [];
			_.forEach(this.form.$('virtualSKUStep.productVirtualSkus').map(field => field), virtualSkuField => {
				if (virtualSkuField.channelDropdownStore.value.extra.channelType.name !== channelType.amazon) {
					virtualSkuField.value.isNonPrime = false;
					virtualSkuField.value.useAmazonInventoryMerge = false;
				}
				virtualSkuField.value.productVirtualSkuCarriers = _.map(virtualSkuField.value.carrierIds, carrierId => {
					return {
						productVirtualSkuId: virtualSkuField.value.id,
						carrierId,
					};
				});
				virtualSkuField.value.carrierIds = null;
				virtualSkus.push(virtualSkuField.value);
			});

			if (this.weightUnitDropdownStore.value.label == 'Pounds and Ounces') {
				let { ounces, pounds } = this.form.values().dimensionsStep;
				this.product.weight = getDecimalPounds(pounds, ounces);
			}
			if (this.parentSellerId) this.product.sellerId = this.parentSellerId;

			let product = {
				...this.product,
				id: this.productId,
				orderLineId: this.orderLineId != null ? this.orderLineId : null,
				productVirtualSkus: virtualSkus,
				isActive: this.productId ? this.isActive : true,
				applyToAllActiveOrders,
			};

			if (this.productIdForPart) {
				product.multiPackageProducts = [
					{
						parentProductId: this.productIdForPart,
						productId: this.productId,
						quantity: this.product.productPartQuantity,
					},
				];
			}

			if (this.productId) {
				await this.listStore.updateProduct(product, sourceOfChange.editProductEditPage);
				this.onFinish(['LOOKUPUPDATE.PRODUCT', { name: this.product.name }]);
			} else {
				await this.listStore.createProduct(product, sourceOfChange.createProductEditPage);
				if (this.orderLineId != null) {
					this.rootStore.notificationStore.success('CREATEPRODUCTANDLINKORDERLINE');
					this.rootStore.viewStore.countUnlinkedOrderLines();
					this.rootStore.routerStore.goTo('master.app.main.order.marketplace.list.detail', {
						id: this.orderDetailId,
					});
				} else {
					this.onFinish('CREATEPRODUCT');
				}
			}
		} catch (err) {
			if (err.statusCode == errorCode.existingResource) {
				this.applyToAllActiveOrdersConfirmModal.close();
				this.resume();
				this.rootStore.notificationStore.error('UNIQUESKUORUPC');
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
		this.resume();
	};

	onWeightUnitChange = value => {
		const { ounces, pounds, weight } = this.form.values().dimensionsStep;
		if (value.label == weightUnitConstants.kilograms) {
			this.form.$('dimensionsStep.weight').set('value', getKilograms(pounds, ounces));
		} else if (weight != '' && weight > 0) {
			const { pounds, ounces } = getPoundsAndOuncesFromKilograms(weight);
			this.form.$('dimensionsStep.pounds').set('value', pounds);
			this.form.$('dimensionsStep.ounces').set('value', ounces);
		}
		this.setRules();
	};

	onPoundsChange = pound => {
		if (pound % 1) {
			this.form.$('dimensionsStep.ounces').set('value', getOunces(pound));
			this.form.$('dimensionsStep.pounds').set('value', getPounds(pound));
		}
		this.setRules();
	};

	@action.bound
	onChannelChange = async parentField => {
		parentField.carrierDropdownStore.clear();
		await parentField.carrierDropdownStore.updateFilter({ channelIds: [parentField.$('channelId').value] });
	};

	@action.bound
	disableUpdateInventory = field => {
		if (field == null || field.extra == null || field.extra.updateInventory == true) {
			return false;
		}
		return true;
	};

	@action.bound
	onFinish = message => {
		this.rootStore.notificationStore.success(message);
		if (this.productIdForPart) {
			this.rootStore.routerStore.goTo('master.app.main.product.list.detail', { id: this.productIdForPart });
		} else {
			this.rootStore.routerStore.goTo('master.app.main.product.list');
		}
	};

	@action.bound
	onDeleteVirtualSkuCallback = field => {
		const idxToRemove = _.findIndex(this.newVirtualSkus, item => {
			return item.value.searchId === field.value.searchId;
		});
		if (idxToRemove !== -1) {
			this.newVirtualSkus.splice(idxToRemove, 1);
		}
	};

	@action.bound handleBackButtonClick = () => {
		if (this.productIdForPart) {
			this.rootStore.routerStore.goTo('master.app.main.product.list.detail', { id: this.productIdForPart });
		} else {
			this.rootStore.routerStore.goTo('master.app.main.product.list');
		}
	};

	@action.bound async openCreateBoxModal() {
		this.openModal({
			data: this,
			component: CreateBox,
		});
	}

	@action.bound updateChannels(item) {
		for (let i = this.form.$(`virtualSKUStep.productVirtualSkus`).fields.data_.size; i > 0; i--) {
			this.form.$(`virtualSKUStep.productVirtualSkus[${i - 1}]`).channelDropdownStore.clear();
		}

		if (item == null) {
			this.form.$(`virtualSKUStep.productVirtualSkus`).fields.forEach(field => {
				field.channelDropdownStore.options = [];
			});
		} else {
			this.form.$(`virtualSKUStep.productVirtualSkus`).fields.forEach(field => {
				field.channelDropdownStore.updateFilter(
					item.value == null ? { embed: ['channelType'] } : { embed: ['channelType'], sellerIds: [item.value] }
				);
			});
		}
	}
}

export default ProductEditViewStore;
